import { Select } from '@radix-ui/react-select';
import { useFocusWithin } from '@react-aria/interactions';
import React, { ChangeEvent } from 'react';
import { InputProps } from '../../design-system/Input';
import {
  InputGroup,
  InputGroupInput,
  InputGroupSlot
} from '../../design-system/InputGroup';
import {
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '../../design-system/Select';
import { assert } from '../../platform/lang/assert';

import { MeasurementOperator, OperatorUnitValue } from './fieldTypes';
import { useTranslation } from 'react-i18next';

export interface MeasurementsFilterFieldProps {
  value: OperatorUnitValue;
  onChange: (value: OperatorUnitValue) => void;
  onBlurWithin?: () => void;
  size: InputProps['size'];
  variant: InputProps['variant'];
}

const operatorOptions = ['gt', 'eq', 'lt'] as const;

const unitOptions = ['cm', 'm', 'ft', 'in'] as const;

function tryRestoreAmount(value: OperatorUnitValue): string {
  switch (value.unit) {
    case 'cm':
    case 'm':
    case 'in':
      return value.amount;
    case 'ft':
      return value.feet;
    default:
      return '';
  }
}

export function MeasurementsFilterField({
  value,
  onChange,
  onBlurWithin,
  size,
  variant
}: MeasurementsFilterFieldProps) {
  const handleOperatorChange = (newOperator: string) => {
    onChange({ ...value, operator: newOperator as MeasurementOperator });
  };

  const handleUnitChange = (newUnit: string) => {
    if (newUnit === 'ft') {
      onChange({
        operator: value.operator,
        unit: 'ft',
        feet: tryRestoreAmount(value),
        inches: '0'
      });
    } else {
      onChange({
        operator: value.operator,
        unit: newUnit as 'cm' | 'm' | 'in',
        amount: tryRestoreAmount(value)
      });
    }
  };

  const handleAmountChange = (e: ChangeEvent<HTMLInputElement>) => {
    assert('amount' in value);
    onChange({ ...value, amount: e.target.value });
  };

  const handleFeetChange = (e: ChangeEvent<HTMLInputElement>) => {
    assert('feet' in value);
    onChange({ ...value, feet: e.target.value });
  };

  const handleInchesChange = (e: ChangeEvent<HTMLInputElement>) => {
    assert('inches' in value);
    onChange({ ...value, inches: e.target.value });
  };

  const { focusWithinProps } = useFocusWithin({ onBlurWithin });
  const { t } = useTranslation('measurements');
  return (
    <div className="@container">
      <div
        {...focusWithinProps}
        className="flex w-full items-center gap-2 @max-[400px]:grid @max-[200px]:flex @max-[250px]:flex-col @max-[250px]:items-stretch"
      >
        <Select value={value.operator} onValueChange={handleOperatorChange}>
          <SelectTrigger
            className="shrink-0 w-auto min-w-[120px] col-span-3"
            size={size}
            variant={variant}
          >
            <SelectValue placeholder="Operator" />
          </SelectTrigger>
          <SelectContent>
            {operatorOptions.map(opt => (
              <SelectItem key={opt} value={opt}>
                {t(`MeasurementsFilterField.operators.${opt}`)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {value.unit === 'ft' ? (
          <>
            <InputGroup
              className="flex-1 shrink-0 text-nowrap"
              size={size}
              variant={variant}
            >
              <InputGroupInput value={value.feet} onChange={handleFeetChange} />
              <InputGroupSlot>
                {t(`MeasurementsFilterField.units.ft`)}
              </InputGroupSlot>
            </InputGroup>

            <InputGroup
              size={size}
              variant={variant}
              className="flex-1 shrink-0 text-nowrap"
            >
              <InputGroupInput
                value={value.inches}
                onChange={handleInchesChange}
              />
              <InputGroupSlot>
                {t(`MeasurementsFilterField.units.in`)}
              </InputGroupSlot>
            </InputGroup>
          </>
        ) : (
          <InputGroup
            size={size}
            variant={variant}
            className="flex-1 col-span-2 shrink-0"
          >
            <InputGroupInput
              value={value.amount}
              onChange={handleAmountChange}
            />
            <InputGroupSlot className={'text-nowrap'}>
              {t(`MeasurementsFilterField.units.${value.unit}`)}
            </InputGroupSlot>
          </InputGroup>
        )}

        <Select value={value.unit} onValueChange={handleUnitChange}>
          <SelectTrigger
            className="shrink-0 w-auto min-w-[120px]"
            size={size}
            variant={variant}
          >
            <SelectValue placeholder="Unit" />
          </SelectTrigger>
          <SelectContent>
            {unitOptions.map(opt => (
              <SelectItem key={opt} value={opt}>
                {t(`MeasurementsFilterField.units.${opt}`, {
                  context: 'long'
                })}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}
